/**
 * "ตั้งค่าตำแหน่ง" (owner, 22 ต.ค. 2569): where the NATASHU artificial-turf field is — a name, an address and its
 * latitude / longitude — kept once in the settings and handed to customers as a map link (`{แผนที่สนาม}`).
 */

export interface VenueSetting {
  name: string;
  address: string;
  lat: number | null;
  lng: number | null;
}

export const VENUE_MAP_TOKEN = '{แผนที่สนาม}';

const inRange = (lat: number, lng: number) => Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
const round = (n: number) => Math.round(n * 1e6) / 1e6;

/**
 * Coordinates out of whatever the shop pastes: "13.7563, 100.5018", a Google Maps link ("…/@13.75,100.50,17z",
 * "…?q=13.75,100.50", "…!3d13.75!4d100.50") or an OpenStreetMap one ("#map=17/13.75/100.50"). Null when there are none.
 */
export function parseLatLng(text: string): { lat: number; lng: number } | null {
  const s = (text ?? '').trim();
  if (!s) return null;
  const tries: RegExp[] = [
    /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/, // the pin of a place link (more exact than the camera)
    /[?&](?:q|query|ll|destination)=(-?\d+(?:\.\d+)?)(?:,|%2C)\s*(-?\d+(?:\.\d+)?)/i,
    /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
    /#map=\d+(?:\.\d+)?\/(-?\d+(?:\.\d+)?)\/(-?\d+(?:\.\d+)?)/,
    /^(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)$/,
  ];
  for (const re of tries) {
    const m = re.exec(s);
    if (!m) continue;
    const lat = Number(m[1]);
    const lng = Number(m[2]);
    if (inRange(lat, lng)) return { lat: round(lat), lng: round(lng) };
  }
  return null;
}

/** The map link customers get: Google Maps, which opens the app on a phone. Null until the place is set. */
export function venueMapUrl(v: Pick<VenueSetting, 'lat' | 'lng'> | null | undefined): string | null {
  if (!v || v.lat == null || v.lng == null) return null;
  return `https://www.google.com/maps/search/?api=1&query=${v.lat},${v.lng}`;
}

/** `{แผนที่สนาม}` → the map link (or the address, or a polite stand-in when the place is not set yet). */
export function fillVenueToken(text: string, v: VenueSetting | null | undefined): string {
  if (!text.includes(VENUE_MAP_TOKEN)) return text;
  const url = venueMapUrl(v);
  const value = url ?? (v?.address.trim() || '(ร้านยังไม่ได้ตั้งตำแหน่งสนาม)');
  return text.split(VENUE_MAP_TOKEN).join(value);
}
